/**
 * PhotoEditModal
 *
 * Per-photo editor that gives the photo the majority of the screen and
 * tucks the caption / credit / URL / skinTone / lighting fields inside.
 * Mirrors the mobile PhotoDetailsModal shape so both platforms feel the
 * same when the tester's ask lands ("big pic, small blurb, edit button").
 *
 * Callers own the underlying photo array + state. This component is a
 * pure controlled dialog: it receives an in-progress photo payload (with
 * any of the editable fields), collects edits in local state, and calls
 * `onSave(patch)` on submit. It never writes to Firestore itself.
 *
 * Works for both "pending" (pre-upload) photos and already-uploaded
 * photos — the caller decides what payload to hand in.
 */

import React, { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';

export interface PhotoEditModalPayload {
  /** Displayable image URI. Pending photos usually pass `preview`
   *  (blob:) or a data-URL; uploaded photos pass the Storage URL. */
  imageUri: string;
  caption?: string;
  creditName?: string;
  creditUrl?: string;
  skinTone?: string;
  lighting?: string;
}

interface PhotoEditModalProps {
  open: boolean;
  photo: PhotoEditModalPayload | null;
  onClose: () => void;
  onSave: (patch: {
    caption: string;
    creditName: string;
    creditUrl: string;
    skinTone: string;
    lighting: string;
  }) => void;
}

const PhotoEditModal: React.FC<PhotoEditModalProps> = ({
  open,
  photo,
  onClose,
  onSave,
}) => {
  const [caption, setCaption] = useState('');
  const [creditName, setCreditName] = useState('');
  const [creditUrl, setCreditUrl] = useState('');
  const [skinTone, setSkinTone] = useState('');
  const [lighting, setLighting] = useState('');

  // Reseed local state whenever a different photo is opened.
  useEffect(() => {
    if (!open || !photo) return;
    setCaption(photo.caption ?? '');
    setCreditName(photo.creditName ?? '');
    setCreditUrl(photo.creditUrl ?? '');
    setSkinTone(photo.skinTone ?? '');
    setLighting(photo.lighting ?? '');
  }, [open, photo?.imageUri]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !photo) return null;

  const handleSave = () => {
    onSave({ caption, creditName, creditUrl, skinTone, lighting });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Big photo — dominates the modal per the tester ask. */}
        <div className="relative">
          <img
            src={photo.imageUri}
            alt="Photo"
            className="w-full aspect-square object-cover rounded-t-2xl"
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 p-1 bg-white/90 rounded-full hover:bg-white"
            title="Close"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Caption
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. 2 coats, no top coat"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Photo credit
              </label>
              <input
                type="text"
                value={creditName}
                onChange={(e) => setCreditName(e.target.value)}
                placeholder="@swatcher"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Credit URL
              </label>
              <input
                type="url"
                value={creditUrl}
                onChange={(e) => setCreditUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Skin tone (optional)
            </label>
            <input
              type="text"
              value={skinTone}
              onChange={(e) => setSkinTone(e.target.value)}
              placeholder="e.g. medium warm, olive undertone"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Lighting (optional)
            </label>
            <input
              type="text"
              value={lighting}
              onChange={(e) => setLighting(e.target.value)}
              placeholder="e.g. natural daylight, ring light"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center justify-center gap-1"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditModal;

/**
 * Utility: build the one-line summary blurb shown under the tile photo
 * when the tile is NOT in edit mode. Only populated fields appear.
 */
export function buildPhotoSummary(photo: Partial<PhotoEditModalPayload>): string {
  const bits: string[] = [];
  if (photo.caption) bits.push(`“${photo.caption}”`);
  if (photo.creditName) bits.push(`by ${photo.creditName}`);
  if (photo.skinTone) bits.push(photo.skinTone);
  if (photo.lighting) bits.push(photo.lighting);
  return bits.join(' · ');
}
